import React from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

// Error boundary for Ward 8 Public Issue Tracker App (PITA) feature pages.
// Public users see a clean, friendly message. Staff (logged-in admins/editors)
// additionally see a detailed debug panel. Raw technical errors are never
// shown to the public.
export class HubErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null, ts: null };
  }

  static getDerivedStateFromError(error) {
    return { error, ts: new Date().toISOString() };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Log for developers only — never rendered to public users
    console.error(`[HubErrorBoundary:${this.props.section || 'Issue Tracker'}]`, error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    let isStaff = false;
    try { isStaff = !!localStorage.getItem('w8_token'); } catch { /* private mode */ }

    const { error, info, ts } = this.state;

    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="hub-error-boundary">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 sm:p-10 text-center">
          <span className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#1a2744] mb-4">
            <RefreshCw className="h-7 w-7 text-[#C9A84C]" />
          </span>
          <h1 className="font-serif font-black text-xl sm:text-2xl text-[#1a2744]">Ward 8 Public Issue Tracker App (PITA)</h1>
          <p className="text-slate-600 mt-2 leading-relaxed" data-testid="hub-error-public-message">
            We are loading PITA, the Ward 8 issue tracker. Please refresh or try again shortly.
          </p>
          <Button onClick={() => window.location.reload()} className="mt-6 h-12 px-6 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="hub-error-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh the page
          </Button>
        </div>

        {isStaff && (
          <div className="mt-6 bg-amber-50 border border-amber-300 rounded-2xl p-5 text-left" data-testid="hub-error-admin-debug">
            <p className="flex items-center gap-2 font-bold text-amber-900 text-sm mb-3">
              <AlertTriangle className="h-4 w-4" /> Admin-only debug panel (not visible to the public)
            </p>
            <dl className="text-xs text-amber-900 space-y-2 font-mono break-all">
              <div><dt className="font-bold uppercase tracking-wide">Failed component</dt><dd>{this.props.section || 'Issue Tracker page'}</dd></div>
              {this.props.apiHint && <div><dt className="font-bold uppercase tracking-wide">Related API</dt><dd>{this.props.apiHint}</dd></div>}
              <div><dt className="font-bold uppercase tracking-wide">Error message</dt><dd>{String(error?.message || error)}</dd></div>
              <div><dt className="font-bold uppercase tracking-wide">Timestamp</dt><dd>{ts}</dd></div>
              {info?.componentStack && (
                <div><dt className="font-bold uppercase tracking-wide">Component stack (first lines)</dt>
                  <dd className="whitespace-pre-wrap">{info.componentStack.split('\n').slice(0, 6).join('\n')}</dd></div>
              )}
            </dl>
          </div>
        )}
      </div>
    );
  }
}

export const withHubBoundary = (element, section, apiHint) => (
  <HubErrorBoundary section={section} apiHint={apiHint}>{element}</HubErrorBoundary>
);
